import { DialogContent } from '@atlas/ui-primitives'
import { X } from 'lucide-react'
import { useId } from 'react'

export interface ImageLightboxProps {
	isOpen: boolean
	/** Full-size image source. A presigned thumbnail URL is fine — it is the same object. */
	src: string
	/** File name, shown as the dialog's title and used as the image's accessible name. */
	name: string
	onClose: () => void
}

/**
 * Full-size preview of one image: a deliverable, or a reference image on a comment.
 *
 * A deliverable thumbnail is too small to judge the artwork that is about to go
 * to validation, so the card's preview opens the image at full size rather than
 * making the reviewer download it to look at it.
 *
 * Presentational (prop-driven), so it renders from a story. Escape and clicking
 * the scrim both dismiss; the panel is surface-less — the artwork is the panel.
 */
export function ImageLightbox({ isOpen, src, name, onClose }: ImageLightboxProps) {
	const titleId = useId()

	return (
		<DialogContent
			size="full"
			isOpen={isOpen}
			onOpenChange={(next) => {
				if (!next) onClose()
			}}
			isDismissable
			aria-labelledby={titleId}
			overlayClassName="bg-ds-text/80 p-6"
			className="relative flex flex-col items-center gap-ds-3 rounded-none bg-transparent p-0 shadow-none"
		>
			<div className="flex w-full items-center justify-between gap-ds-4">
				<h2 id={titleId} className="truncate font-mono text-ds-xs text-ds-primary-foreground/80">
					{name}
				</h2>
				<button
					type="button"
					onClick={onClose}
					aria-label="Close preview"
					className="flex-none rounded-ds-sm p-1 text-ds-primary-foreground/70 transition-colors hover:text-ds-primary-foreground"
				>
					<X className="h-5 w-5" />
				</button>
			</div>
			{/* Bounded by the viewport, not the natural image size, so a 4000px
			    render is fully visible instead of overflowing the dialog. */}
			<img src={src} alt={name} className="max-h-[85vh] max-w-full rounded-ds-control object-contain shadow-ds-lg" />
		</DialogContent>
	)
}
